import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { times } from 'lodash'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  faCalendarClock,
  faCalculator,
  faMoneyCheckDollar,
  faPlus,
} from '@fortawesome/pro-light-svg-icons'
import { faBullseye } from '@fortawesome/pro-regular-svg-icons'
import { Toaster } from 'sonner'

import {
  Alert,
  Button,
  Card,
  CardColors,
  Checkbox,
  Dropdown,
  handleToast,
  Icon,
  Popup,
  Select,
  Table,
  Text,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  useFetchResponse,
  useReselector,
  useTimeoutRef,
} from '@/utils/sharedHooks'
import {
  fetchUserIncomeMonthsForYear,
  selectIncomePlanningMonthsForYear,
  selectIncomePlanningPlannedIncomeForYear,
  UserIncomePlanMonth,
} from '@/features/income-planning/incomePlanning.slice'
import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { IncomeMonthSheet } from '@/features/income-planning/income-planning-months/IncomeMonthSheet'
import { ApplyPlanDialog } from '@/features/income-planning/income-planning-months/ApplyPlanDialog'
import { DifferenceArrow } from '@/features/income-planning/DifferenceArrow'
import { CreateFromExistingOrEditDialog } from '@/features/income-planning/income-planning-months/CreateFromExistingOrEditDialog'
import { IncomePlanningIntro } from '@/features/income-planning/income-planning-months/IncomePlanningIntro'
import { useLocalStorageBool } from '@/utils/localStorage'
import { IncomePlanningMonthIntroCards } from '@/features/income-planning/income-planning-months/IncomePlanningMonthIntroCards'
import {
  IncomeForMonthPopup,
  PlannedIncomePopup,
  YearIncomePopup,
  YearToDateIncomePopup,
} from '@/features/income-planning/IncomePlanningPopups'

const INCOME_PLANNING_MONTHS_KEY = 'INCOME_PLANNING_MONTHS_KEY'

const CLEAR_UPDATE_MONTH_MS = 7000

// todo checkbox has bad offset
export const IncomePlanningMonths = () => {
  const navigate = useNavigate()
  const { state: locationState } = useLocation()

  const fp = useReselector(getFinancialProfile)

  const {
    isTrue: incomePlanningIntroDismissed,
    setTrue: setIncomePlanningDismissed,
  } = useLocalStorageBool(INCOME_PLANNING_MONTHS_KEY)

  const currentDate = useMemo(() => DateTime.now(), [])
  const [selectedYear, setSelectedYear] = useState(currentDate.year)
  const [monthToShow, setMonthToShow] = useState<UserIncomePlanMonth | null>(
    null
  )
  const [selectedMonths, setSelectedMonths] = useState<number[]>([])

  const [updatedMonths, setUpdatedMonths] = useState<number[]>(
    locationState?.updatedPlansIds || []
  )

  useEffect(() => {
    if (!locationState?.updatedPlansIds?.length) {
      if (locationState?.planCreated) {
        handleToast({
          title: 'Planned created',
          description: 'Your plan has been created.',
          variant: 'success',
        })
      } else if (locationState?.planUpdated) {
        handleToast({
          title: 'Updated',
          description: 'We’ve updated your annual goals.',
          variant: 'success',
        })
      }
    }
  }, [
    locationState?.planCreated,
    locationState?.planUpdated,
    locationState?.updatedPlansIds?.length,
  ])

  const timeout = useTimeoutRef()
  useEffect(() => {
    if (updatedMonths.length) {
      // This is cleared after the timeout is complete to unhighlight the updated rows
      timeout.current = setTimeout(() => {
        setUpdatedMonths([])
      }, CLEAR_UPDATE_MONTH_MS)

      handleToast({
        title: 'Planned income updated',
        description: 'We’ve updated your monthly planned income.',
        variant: 'success',
      })
    }
  }, [timeout, updatedMonths.length])

  const incomePlaningMonths = useReselector(
    selectIncomePlanningMonthsForYear,
    selectedYear
  )

  const incomePlaningMonthsRes = useFetchResponse(
    fetchUserIncomeMonthsForYear,
    selectedYear
  )

  const plannedIncomeRollupForSelectedYear = useReselector(
    selectIncomePlanningPlannedIncomeForYear,
    selectedYear
  )

  const annualIncomeGoalInCents = fp?.annualIncomeGoalInCents

  const currentYearSelected =
    currentDate.year.toString() === selectedYear.toString()

  const incomeGoalCardBottom = useMemo(() => {
    if (
      currentYearSelected && // Goal can only be set for current year
      plannedIncomeRollupForSelectedYear &&
      (!annualIncomeGoalInCents || // If the user hasn't set an income goal
        annualIncomeGoalInCents < plannedIncomeRollupForSelectedYear.intValue) // if new total is greater than old goal
    ) {
      return (
        <Alert variant="priority" customIconName={faBullseye} className="mt-4">
          <div className="flex">
            <Text variant={TypographyStyles.LABEL_MD}>
              Update annual goals.
            </Text>
            <Button
              onClick={() => navigate('/income-planning/update-goal')}
              variant="link"
              className="pt-0 h-7"
            >
              Go →
            </Button>
          </div>
        </Alert>
      )
    }

    return (
      <Button
        onClick={() => navigate('/goal-setting')}
        variant="link"
        className="mt-4 pl-0"
      >
        View annual goals →
      </Button>
    )
  }, [
    annualIncomeGoalInCents,
    currentYearSelected,
    navigate,
    plannedIncomeRollupForSelectedYear,
  ])

  const pastYearCardValue = useMemo(() => {
    if (currentYearSelected) {
      if (incomePlaningMonthsRes?.pastYearTotalBusinessIncomeInCents) {
        return formatCurrencyFromCents(
          incomePlaningMonthsRes.pastYearTotalBusinessIncomeInCents
        )
      }
    } else {
      if (incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents) {
        return formatCurrencyFromCents(
          incomePlaningMonthsRes.currentYearTotalBusinessIncomeInCents
        )
      }
    }

    return '-'
  }, [
    currentYearSelected,
    incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents,
    incomePlaningMonthsRes?.pastYearTotalBusinessIncomeInCents,
  ])

  if (!incomePlanningIntroDismissed) {
    return <IncomePlanningIntro onClose={setIncomePlanningDismissed} />
  }

  return (
    <>
      <Toaster />
      <IncomePlanningMonthIntroCards />
      <Text as={TypographyStyles.DISPLAY_XS}>Income Planning</Text>
      <div className="mt-10 w-25">
        <Select
          value={selectedYear}
          onValueChange={setSelectedYear}
          options={[
            { text: currentDate.year.toString(), value: currentDate.year },
            {
              text: (currentDate.year + 1).toString(),
              value: currentDate.year + 1,
            },
          ]}
        />
      </div>
      <div className="flex gap-4 mt-4">
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={faCalendarClock} backgroundClassName="justify-start" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {pastYearCardValue}
          </Text>
          <Popup
            content={<YearIncomePopup year={selectedYear - 1} />}
            trigger={
              <Text className="mt-1" variant={TypographyStyles.POPUP_LINK}>
                {selectedYear - 1} Income
              </Text>
            }
          />
          <br />
          <Button
            variant="link"
            onClick={() => navigate('/insights/income')}
            className="mt-4 pl-0"
          >
            View income breakdown →
          </Button>
        </Card>
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={faCalculator} backgroundClassName="justify-start" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {plannedIncomeRollupForSelectedYear
              ? formatCurrency(plannedIncomeRollupForSelectedYear)
              : '-'}
          </Text>
          <Popup
            content={<PlannedIncomePopup />}
            trigger={
              <Text variant={TypographyStyles.POPUP_LINK} className="mt-1">
                {selectedYear} Planned Income
              </Text>
            }
          />
          <br />
          {incomeGoalCardBottom}
        </Card>
        <Card className="w-1/3" color={CardColors.LEVEL_1}>
          <Icon icon={faMoneyCheckDollar} backgroundClassName="justify-start" />
          <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
            {incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents &&
            currentYearSelected
              ? formatCurrencyFromCents(
                  incomePlaningMonthsRes.currentYearTotalBusinessIncomeInCents
                )
              : '-'}
          </Text>
          <Popup
            content={<YearToDateIncomePopup year={selectedYear} />}
            trigger={
              <Text variant={TypographyStyles.POPUP_LINK} className="mt-1">
                {selectedYear} Year-to-date Income
              </Text>
            }
          />
          <br />
          {fp?.annualIncomeGoalInCents &&
          incomePlaningMonthsRes?.currentYearTotalBusinessIncomeInCents &&
          currentYearSelected ? (
            <Text variant={TypographyStyles.BODY_SM} className="mt-4">
              <b>
                {(
                  (incomePlaningMonthsRes.currentYearTotalBusinessIncomeInCents /
                    fp.annualIncomeGoalInCents) *
                  100
                ).toFixed(2)}
                %
              </b>{' '}
              to income goal of{' '}
              <b>{formatCurrencyFromCents(fp.annualIncomeGoalInCents)}</b>
            </Text>
          ) : null}
        </Card>
      </div>
      <div className="flex justify-between mt-10">
        <ApplyPlanDialog
          selectedMonths={selectedMonths}
          selectedYear={selectedYear}
          setSelectedMonths={setSelectedMonths}
          setUpdatedMonths={setUpdatedMonths}
        />
        <Dropdown>
          <Dropdown.Trigger>
            <Button iconLeft={faPlus}>Create Plan</Button>
          </Dropdown.Trigger>
          <Dropdown.Content>
            <Dropdown.Item
              onClick={() => navigate('/income-planning/create-plan')}
            >
              <Text variant={TypographyStyles.BODY_SM}>
                Create new template
              </Text>
            </Dropdown.Item>
            <CreateFromExistingOrEditDialog createFromExisting />
            <CreateFromExistingOrEditDialog createFromExisting={false} />
          </Dropdown.Content>
        </Dropdown>
      </div>
      <Table className="mt-4">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width="4%">
              <Checkbox
                checked={selectedMonths.length === 12}
                onCheckedChange={() =>
                  setSelectedMonths(
                    selectedMonths.length === 12
                      ? []
                      : times(12, (val) => val + 1)
                  )
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell width="11%">
              <Text variant={TypographyStyles.EYEBROW}>Date</Text>
            </Table.HeaderCell>
            <Table.HeaderCell width="20%">
              <Popup
                content={<YearIncomePopup year={selectedYear - 1} />}
                trigger={
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    className="border-b-2 border-green-50 border-dotted"
                  >
                    {selectedYear - 1} Income
                  </Text>
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell width="20%">
              <Popup
                content={<PlannedIncomePopup />}
                trigger={
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    className="border-b-2 border-green-50 border-dotted"
                  >
                    Planned Income
                  </Text>
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell width="20%">
              <Popup
                content={<IncomeForMonthPopup year={selectedYear} />}
                trigger={
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    className="border-b-2 border-green-50 border-dotted"
                  >
                    {selectedYear} Income
                  </Text>
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell width="25%">
              <Text variant={TypographyStyles.EYEBROW}>Notes</Text>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        {incomePlaningMonths.map((planningMonth) => {
          const percentDifference =
            planningMonth.totalPlannedIncomeInCents &&
            planningMonth.currentTotalBusinessIncomeInCents
              ? ((planningMonth.currentTotalBusinessIncomeInCents -
                  planningMonth.totalPlannedIncomeInCents) /
                  planningMonth.totalPlannedIncomeInCents) *
                100
              : 0

          return (
            <Table.Row
              key={planningMonth.month}
              className={`transition duration-300 ease-in ${updatedMonths.includes(planningMonth.id) ? 'bg-moss-25' : ''}`}
            >
              <Table.Cell>
                <Checkbox
                  checked={selectedMonths.includes(planningMonth.month)}
                  onCheckedChange={(val) => {
                    if (val) {
                      setSelectedMonths([
                        ...selectedMonths,
                        planningMonth.month,
                      ])
                    } else {
                      setSelectedMonths(
                        selectedMonths.filter(
                          (month) => month !== planningMonth.month
                        )
                      )
                    }
                  }}
                />
              </Table.Cell>
              <Table.Cell>
                <Button
                  variant="link"
                  onClick={() => setMonthToShow(planningMonth)}
                  className="pl-0"
                >
                  {DateTime.fromObject({ month: planningMonth.month }).toFormat(
                    DATE_FORMATS_LUXON.MONTH
                  )}
                </Button>
              </Table.Cell>
              <Table.Cell>
                <Text variant={TypographyStyles.BODY_SM}>
                  {planningMonth.pastTotalBusinessIncomeInCents
                    ? formatCurrencyFromCents(
                        planningMonth.pastTotalBusinessIncomeInCents
                      )
                    : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Button
                  variant="link"
                  onClick={() => setMonthToShow(planningMonth)}
                  className="pl-0"
                >
                  {planningMonth.totalPlannedIncomeInCents
                    ? formatCurrencyFromCents(
                        planningMonth.totalPlannedIncomeInCents
                      )
                    : 'Add Amount'}
                </Button>
              </Table.Cell>
              <Table.Cell>
                <div className="flex">
                  <Text variant={TypographyStyles.BODY_SM} className="mr-4">
                    {planningMonth.currentTotalBusinessIncomeInCents
                      ? formatCurrencyFromCents(
                          planningMonth.currentTotalBusinessIncomeInCents
                        )
                      : '-'}
                  </Text>

                  {Boolean(percentDifference) && (
                    <DifferenceArrow
                      isUpward={percentDifference > 0}
                      text={`${Math.abs(percentDifference).toFixed(1)}%`}
                      textStyle={TypographyStyles.LABEL_SM}
                    />
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Text variant={TypographyStyles.BODY_SM}>
                  {planningMonth.notes || '-'}
                </Text>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table>
      <IncomeMonthSheet
        monthToShow={monthToShow}
        closeSheet={() => setMonthToShow(null)}
      />
    </>
  )
}
